import React, { useEffect, useState, useRef } from 'react';
import {useLocation} from 'react-router-dom';
import { DocumentsTableHeader } from 'components/DocumentsTableHeader';
import { DocumentsTableRow } from 'components/DocumentsTableRow';
import { SummaryRow } from 'components/SummaryRow';

import styles from 'css_modules/DocumentsTable.module.css';
import { ProgressSpinner } from "./ProgressSpinner";

export const DocumentsTable = ({
    checkedDocs,
    docs,
    focusedDocId,
    mainChecked,
    setCheckedDocs,
    setMainChecked,
    setOrdering,
    visibleState,
    user,
    fields,
    visibleFields,
    loading,
    ordering,
    docsCustomTypes,
    ...tableRowProps
}) => {
    const location = useLocation();
    const containerRef = useRef(null);

    useEffect(() => {
        const lastReloaded = localStorage.getItem('lastReloaded');
        const now = new Date().getTime();

        if (!lastReloaded) {
            window.location.reload(true);
            localStorage.setItem('lastReloaded', now.toString());
        } else {
            const diff = now - parseInt(lastReloaded);
            const oneHour = 3600000;

            if (diff > oneHour) {
                window.location.reload(true);
                localStorage.setItem('lastReloaded', now.toString());
            }
        }
    }, []);

    const visibleCheckbox = true;

    const [columnWidths, setColumnWidths] = useState(() => {
        const stored = localStorage.getItem('documentTableWidths' + user.pk + location.pathname);

        if (stored) return JSON.parse(stored);

        return visibleFields.map((item) => {
            const keys = item.split(/\.(.*)/);
            return fields[keys[0]].items[keys[1]].width || '200px';
        });
    });

    


    useEffect(() => {
        localStorage.setItem(
            'documentTableWidths' + user.pk +location.pathname,
            JSON.stringify(columnWidths)
        );
    }, [columnWidths, user.pk,location.pathname]);

    const getTableClassName = () => {
        if (!visibleCheckbox) return styles.tableWithoutCheckbox;
        if (!visibleState) return styles.tableWithoutState;
        return styles.tableFull;
    };

    const getAutoFitWidth = (colIndex) => {
        const rows = document.querySelectorAll('tbody tr');

        let maxWidth = 60;

        rows.forEach((row) => {
            const cell = row.children[(visibleCheckbox ? 1 : 0) + colIndex];

            if (!cell) return;
            
            const text = cell.querySelector('span');
            
            if (!text) return;
            
            maxWidth = Math.max(maxWidth, text.scrollWidth);
            
        });

        return maxWidth + 24;
    };

    const autoFitColumn = (colIndex) => {
        const newWidths = [...columnWidths];
        let nw = getAutoFitWidth(colIndex)
        newWidths[colIndex] = `${nw}px`;
        
        setColumnWidths(newWidths);
        return nw
    };

    const stretchColumnsToFit = () => {
        if (!containerRef.current) return;

        const totalWidth = containerRef.current.offsetWidth;

        const checkboxWidth = visibleCheckbox ? 28 : 0;
        const fillerWidth = 80;

        const available = totalWidth - checkboxWidth - fillerWidth;

        const minWidths = visibleFields.map((_, colIndex) => {
            const headerCells = document.querySelectorAll('th');

            const headerEl =
                headerCells[colIndex]?.querySelector?.(`.${styles.titleBlock}`);

            const headerMin = headerEl
                ? Math.ceil(headerEl.scrollWidth) + 20
                : 120;

            //const currentAuto = getAutoFitWidth(colIndex);

            return headerMin;
        });

        const minTotal = minWidths.reduce((a, b) => a + b, 0);

        if (minTotal >= available) {
            setColumnWidths(minWidths.map(w => `${w}px`));
            return;
        }

        const extra = available - minTotal;

        const scaled = minWidths.map(w => {
            const share = w / minTotal;
            return Math.floor(w + extra * share);
        });

        setColumnWidths(scaled.map(w => `${w}px`));
    };

    if (visibleFields.length!==columnWidths.length){
        stretchColumnsToFit()
    }

    const isStavColumn = (item) => {
        const keys = item.split(/\.(.*)/);
        return fields[keys[0]].items[keys[1]].name === 'Stav';
    };

    const ormFormat = (str) => {
        let ormFormatedStr = str.replace("document_info", 'info').replace('.', '__');
        if(str === 'note'){
            ormFormatedStr = 'info__note';
        }
        return ormFormatedStr;
    }

    const drawHeader = () => {
        return visibleFields.map((item, index) => {
            const keys = item.split(/\.(.*)/);
            
            let selected = 0;

            if (ordering.endsWith(ormFormat(keys[1])) && !ordering.startsWith('-')    ) {
                selected = -1
            }
            if (ordering.endsWith(ormFormat(keys[1])) && ordering.startsWith('-')    ) {
                selected = 1
            }

            const isStav = isStavColumn(item);
            const minStavWidth = isStav ? getAutoFitWidth(index) : null;

            return (
                <DocumentsTableHeader
                    key={'h' + keys[1]}
                    index={index}
                    columnWidths={columnWidths}
                    setColumnWidths={setColumnWidths}
                    autoFitColumn={autoFitColumn}
                    setOrdering={setOrdering}
                    ordering={ordering}
                    orderBy={ormFormat(keys[1])}
                    selected={selected}
                    forceMinWidth={minStavWidth}
                >
                    {fields[keys[0]].items[keys[1]].name}
                </DocumentsTableHeader>
            );
        });
    };

    const styleColumns = () => ({
        gridTemplateColumns:
            (visibleCheckbox ? '1.75rem ' : '') +
            columnWidths.join(' ') +
            ' 3.5rem'
    });

    
    if (loading) return <ProgressSpinner />;

    return (
        <>
            <div className={styles.content} ref={containerRef}>
                <div
                    style={{
                        position: "sticky",
                        top: 0,
                        zIndex: 21,
                        background: "white"
                    }}
                >
                    <div style={{ display: "flex", width: "100%" }}>
                        <table
                            className={getTableClassName()}
                            style={{
                                ...styleColumns(),
                                tableLayout: "fixed"
                            }}
                        >
                            <tbody>
                                <tr>
                                    {visibleCheckbox && (
                                        <td className={styles.checkboxCell}>
                                            <input
                                                type="checkbox"
                                                checked={mainChecked}
                                                onChange={(e) => {
                                                    setMainChecked(e.target.checked);
                                                    const activeDocs =
                                                        docs.filter(doc => (location.pathname === '/documents' || doc.is_active || location.pathname === '/archive' || location.pathname === '/my_templates') && !doc.awaiting_capture);
                                                    setCheckedDocs(
                                                        e.target.checked
                                                            ? activeDocs.map(d => d.uuid)
                                                            : []
                                                    );
                                                }}
                                            />
                                        </td>
                                    )}

                                    {drawHeader()}

                                    <div
                                        style={{
                                            width: "3.5rem",
                                            position: "sticky",
                                            right: 0,
                                            zIndex: 30,
                                            marginLeft:"-0.1rem",
                                            backgroundColor: "var(--light)",
                                            borderBottom: "1px solid var(--semilight)",
                                            borderLeft: "2px solid var(--semilight)",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center"
                                        }}
                                    >
                                        <button
                                            onClick={stretchColumnsToFit}
                                            style={{
                                                background: "transparent",
                                                border: "none",
                                                padding: 0,
                                                cursor: "pointer",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                width: "100%",
                                                height: "100%"
                                            }}
                                        >
                                            <svg
                                                width="20"
                                                height="20"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    d="M9 6L4 12L9 18"
                                                    stroke="var(--purple)"
                                                    strokeWidth="3"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                                <path
                                                    d="M15 6L20 12L15 18"
                                                    stroke="var(--purple)"
                                                    strokeWidth="3"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </button>
                                    </div>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <table
                    className={styles.tableBodyScrollable}
                    style={styleColumns()}
                >
                    <tbody>
                        {docs.map((doc, index) => (
                            <DocumentsTableRow
                                key={doc.uuid}
                                doc={doc}
                                index={index / docs.length}
                                docsCustomTypes={docsCustomTypes}
                                focused={focusedDocId === doc.uuid}
                                visibleCheckbox={visibleCheckbox}
                                visibleState={visibleState}
                                checkedDocs={checkedDocs}
                                setCheckedDocs={setCheckedDocs}
                                setMainChecked={setMainChecked}
                                user={user}
                                fields={fields}
                                visibleFields={visibleFields}
                                {...tableRowProps}
                            />
                        ))}
                    </tbody>
                </table>

                <div className={styles.stretch} />
            </div>

            <table className={styles.summaryRow} style={styleColumns()}>
                <tbody>
                    <SummaryRow
                        docs={docs}
                        visibleFields={visibleFields}
                        visibleCheckbox={visibleCheckbox}
                    />
                </tbody>
            </table>
        </>
    );
};